import { TextButton } from '@creditornot/cb-button';
import { InputAlert } from '@creditornot/cb-input';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Dropzone from './Dropzone';
import { ASPECT_RATIO, MIN_WIDTH } from './constants';
import { getImageSize } from './utils';

type Props = {
  className?: string;
  background?: React.ReactNode;
  onCancel?: () => void;
  onSelectFile: (file: File) => void;
};

const Preview = styled.div`
  overflow: hidden;
  border-radius: 8px;
`;

const DropzoneContainer = styled.div`
  position: relative;
  ${Preview} {
    opacity: 0.12;
  }
`;

const StyledDropzone = styled(Dropzone)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const CancelButton = styled(TextButton).attrs({ variant: 'grey' })`
  margin-block-start: 16px;
`;

const ErrorAlert = styled(InputAlert).attrs({ variant: 'error' })`
  margin-block-start: 16px;
`;

const FileSelector = ({ className, background, onSelectFile, onCancel }: Props) => {
  const [error, setError] = useState<React.ReactNode>(null);

  const handleSelectFile = async (files: File[]) => {
    const [file] = files;
    let imageSize: { width: number; height: number };

    try {
      imageSize = await getImageSize(file);
    } catch (_) {
      setError(<FormattedMessage id="errors.unknown-error-occurred" />);
      return;
    }

    const minHeight = Math.floor(MIN_WIDTH / ASPECT_RATIO);
    if (imageSize.width < MIN_WIDTH || imageSize.height < minHeight) {
      setError(
        <FormattedMessage
          id="image-upload.errorTooSmall"
          values={{
            width: MIN_WIDTH,
            height: minHeight,
          }}
        />,
      );
      return;
    }

    onSelectFile(file);
  };

  return (
    <div className={className}>
      {background ? (
        <DropzoneContainer>
          <Preview>{background}</Preview>
          <StyledDropzone onDropAccepted={handleSelectFile} mode="single-image" />
        </DropzoneContainer>
      ) : (
        <Dropzone onDropAccepted={handleSelectFile} mode="single-image" />
      )}
      {onCancel && (
        <CancelButton variant="grey" onClick={onCancel}>
          <FormattedMessage id="image-upload.cancel" />
        </CancelButton>
      )}
      {error && <ErrorAlert>{error}</ErrorAlert>}
    </div>
  );
};

export default FileSelector;
